import * as THREE from "three";
import { MapControls } from "three/examples/jsm/controls/MapControls.js";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader.js";

import { Line2 } from "three/addons/lines/Line2.js";
import { LineMaterial } from "three/addons/lines/LineMaterial.js";
import { LineGeometry } from "three/addons/lines/LineGeometry.js";

import { initReefsPopup } from "./reefs_list/reefs_list.js";
import { put_geometry_all_reefs, marcadoresRutas, vincularMarcadorAlReef } from "./reefs_list/buscar_arrecife.js";
import { vincularMarcadorAlSites, marcadoresRutas_fuera_agua } from "./out_sites/sitios_fuera_agua.js"; 
import { checkTerrainCollision } from './colicionadores.js';
import { color_terrain_arena, populate_palms_eco } from "./painting_terrain.js"; 

import { svg } from "./icons.js";
import { handleTerrainDoubleClick } from "./click_manager.js";
import { generarMapaCielo } from "./skybox.js";
import { extension_terrain } from './extension_terrain.js'; 
import { add_light } from "./luz_ambiente.js";
import { cargarNubes } from "./crear_nubes.js";
import { agregar_agua, agregar_muros, remove_water } from "./crear_agua.js";
import { renderManager } from './globals.js';
import { tutorial } from "./tutorial.js";
import { camera_positions } from "./camera_positions.js";
import { vueloInicial } from "./vuelo_inicial.js";

var URL_GLOBAL = 'https://cozumel-reef-maps.com.mx/';

console.log("Three.js Revision:", THREE.REVISION);

let camera, controls, scene, renderer;
renderManager.needsRender = false;

let terrainMesh = null; 
const collidableMeshes = []; 
let surfaceGroup = null; 
let agua = null; 

let actualizarVientoPalmeras = null;

const raycaster = new THREE.Raycaster(); 
const preloader = document.getElementById('preloader');
const preloaderText = document.getElementById('preloader-text');
const loaderProgress = document.getElementById('loader-progress');

const mouse = new THREE.Vector2();
let fps = 60;
let updatePivot = true; 

const targetFPS = 60;
const frameInterval = 1000 / targetFPS;

let lastUserMove = 0;
let lerpPivotFactor = 0.25; 
const pivotDistance = 0.5;  
const PIVOT_DELAY = 100;

init();

function init() {
  scene = new THREE.Scene();
  renderManager.scene = scene;

  document.getElementById("controlsUI").innerHTML = `
      <button class="btn font_responsive" id="btn-home">🏠</button>
      <button class="btn font_responsive" id="btn-camera">📹</button>
      <button class="btn font_responsive" id="btn-tutorial">${svg.navegacion_icon}</button>
  `;

  document.getElementById("btn-home").addEventListener("click", home);
  document.getElementById("btn-camera").addEventListener("click", camera_positions);
  document.getElementById("btn-tutorial").addEventListener("click", tutorial);

  document.getElementById("nombre_arrecife").innerHTML = "Cozumel Map";
  document.getElementById("descripcion_arrecife").innerHTML = "Cozumel Map";

  const isMobile = /Mobi|Android/i.test(navigator.userAgent);
  const lowEndMobile = isMobile && (
      window.devicePixelRatio <= 1 ||
      (navigator.deviceMemory && navigator.deviceMemory <= 2)
  );

  let pixelRatio = Math.min(window.devicePixelRatio, 2);
  if (isMobile) pixelRatio = Math.min(window.devicePixelRatio, 1.5);
  if (lowEndMobile) pixelRatio = 1.2;

  renderer = new THREE.WebGLRenderer({
      antialias: !lowEndMobile,
      preserveDrawingBuffer: false   
  });
  renderer.setPixelRatio(pixelRatio);
  renderer.setSize(window.innerWidth, window.innerHeight);
  document.body.appendChild(renderer.domElement);

  let lastTime = performance.now();
  let frames = 0;
  setInterval(() => {
      const now = performance.now();
      fps = (frames * 1000) / (now - lastTime);
      frames = 0;
      lastTime = now;
  }, 1000);

  if (lowEndMobile) {
      renderManager.needsRender = true;
      controls && controls.addEventListener("change", () => { renderManager.needsRender = true; });
      window.addEventListener("dblclick", () => { renderManager.needsRender = true; });
  }

  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = lowEndMobile ? 128 : 256;
  const ctx = canvas.getContext("2d");
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0.25, "#00cfff");   
  gradient.addColorStop(0.50, "#00cfff");
  gradient.addColorStop(0.70, "#0066ff"); 
  gradient.addColorStop(0.95, "#001133"); 
  gradient.addColorStop(1, "#000a22");     
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 1, canvas.height);
  scene.background = new THREE.CanvasTexture(canvas);

  const ambientLight = new THREE.AmbientLight(0xffffff, 0.6); 
  scene.add(ambientLight);

  const sunLight = new THREE.DirectionalLight(0xffffff, 1.2); 
  sunLight.position.set(5, 10, 7); 
  scene.add(sunLight);

  camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.001, 1000); 
  camera.position.set(0, 0, 0);
  renderManager.camera = camera;

  controls = new MapControls(renderManager.camera, renderer.domElement);
  renderManager.controls = controls;
  renderManager.controls.enableDamping = true;
  renderManager.controls.dampingFactor = 0.12;
  renderManager.controls.rotateSpeed = 0.4;
  renderManager.controls.zoomSpeed = 0.5;
  renderManager.controls.panSpeed = 0.4;
  renderManager.controls.screenSpacePanning = false;
  renderManager.controls.minDistance = 0.001;
  renderManager.controls.maxDistance = 100;
  renderManager.controls.maxPolarAngle = Math.PI;
  renderManager.controls.addEventListener("change", () => { renderManager.needsRender = true; });

  if (isMobile) {
    const basePanSpeed = 0.0015;
    if (typeof renderManager.controls.panLeft === "function" && typeof renderManager.controls.panUp === "function") {
      const panLeft = renderManager.controls.panLeft.bind(renderManager.controls);
      const panUp = renderManager.controls.panUp.bind(renderManager.controls);
      renderManager.controls.panLeft = function(distance, objectMatrix) { panLeft(distance * basePanSpeed, objectMatrix); };
      renderManager.controls.panUp = function(distance, objectMatrix) { panUp(distance * basePanSpeed, objectMatrix); };
    }
  }

  marcadoresRutas.length = 0;
  marcadoresRutas_fuera_agua.length = 0;

  load_model();
}

function load_model() {
    const loader = new FBXLoader();
    loader.load('/reefs_obj/COZUMEL_FULL_MAP_NEW.fbx',
      (object) => {
        surfaceGroup = new THREE.Group();

        object.traverse((child) => {
          if (child.isMesh && child.name === "TERRAIN") {
              terrainMesh = child;
              renderManager.terrainMesh = terrainMesh;

          } else if (child.name === "FLECHA_N_S" || child.name === "FLECHA_W_E" || child.name === "E" || child.name === "N" || child.name === "S" || child.name === "W") {
            const materiales = Array.isArray(child.material) ? child.material : [child.material];
            materiales.forEach(mat => {
                if (mat.color) {
                    mat.color.setHex(0xff0000);
                    mat.needsUpdate = true;
                }
            });
            return;

          } else if (child.isMesh && child.geometry && child.geometry.attributes.position) {
            child.updateMatrixWorld(true);
            const position = child.geometry.attributes.position;
            const color = new THREE.Color();
            const colors = [];
            const v = new THREE.Vector3();

            let minY = Infinity, maxY = -Infinity;
            for (let i = 0; i < position.count; i++) {
              v.set(position.getX(i), position.getY(i), position.getZ(i));
              v.applyMatrix4(child.matrixWorld);
              const wy = v.y;
              minY = Math.min(minY, wy);
              maxY = Math.max(maxY, wy);
            }

            const darkBase   = new THREE.Color(0x0f1d29);
            const midStone   = new THREE.Color(0x425b5a);
            const lightStone = new THREE.Color(0xbfd2c2);

            function noise(x, y, z) { return (Math.sin(x * 3.1 + y * 2.7 + z * 4.3) * 0.5 + 0.5); }
            function noiseDetail(x, y, z) {
              return (noise(x, y, z) * 0.6 + noise(x * 2, y * 2, z * 2) * 0.3 + noise(x * 4, y * 4, z * 4) * 0.1);
            }

            for (let i = 0; i < position.count; i++) {
              v.set(position.getX(i), position.getY(i), position.getZ(i));
              v.applyMatrix4(child.matrixWorld);
              let t = (maxY > minY) ? THREE.MathUtils.clamp((v.y - minY) / (maxY - minY), 0, 1) : 0;

              color.copy(darkBase).lerp(midStone, t * 0.85).lerp(lightStone, Math.pow(t, 1.5) * 0.9);
              const n = noiseDetail(position.getX(i) * 0.18, position.getY(i) * 0.18, position.getZ(i) * 0.18);
              color.multiplyScalar(0.82 + n * 0.18);

              if (noise(position.getX(i) * 0.05, position.getY(i) * 0.05, position.getZ(i) * 0.05) < 0.34) color.multiplyScalar(0.7);
              color.offsetHSL(0, -0.02, (noise(position.getX(i) * 1.8, position.getY(i) * 1.8, position.getZ(i) * 1.8) - 0.5) * 0.12);
              color.multiplyScalar(1.0 - (1.0 - t) * 0.45);

              colors.push(color.r, color.g, color.b);
            }

            child.geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
            child.material = new THREE.MeshStandardMaterial({
              vertexColors: true,
              color: new THREE.Color(0x0f1d29), 
              metalness: 0.04,
              roughness: 0.96,
              flatShading: false
            });
            collidableMeshes.push(child);
          }
        });

        const boxTemp = new THREE.Box3().setFromObject(object);
        const sizeTemp = boxTemp.getSize(new THREE.Vector3());
        const escalaReal = 1 / sizeTemp.x;
        
        object.scale.set(escalaReal, escalaReal, escalaReal);
        boxTemp.setFromObject(object);

        const center = boxTemp.getCenter(new THREE.Vector3());
        object.position.sub(center);
        
        object.updateMatrixWorld(true);

        object.traverse((child) => {
            if (child.isMesh && child.name.startsWith("REEF_")) { vincularMarcadorAlReef(child, scene); }
            if (child.isMesh && child.name.startsWith("BEACH_")) { vincularMarcadorAlSites(child, scene); }
            if (child.isMesh && child.name && child.name.toUpperCase() === "CUBE_SURFACE") {
              child.updateMatrixWorld(true);
              const pos = new THREE.Vector3();
              child.getWorldPosition(pos);
              renderManager.surfaceY = pos.y;

              const meshClone = child.clone();
              surfaceGroup.add(meshClone);
            }
        });

        scene.add(surfaceGroup);
        scene.add(object);

        loaderProgress.style.width = '100%';
        preloaderText.innerText = 'Cozumel Map\nLoading: 100%';

        setTimeout(() => {
            preloader.style.display = 'none';
            vueloInicial();
        }, 300);

        agregar_muros(terrainMesh, scene);
        agregar_agua(scene, terrainMesh, agua);
        cargarNubes(object, scene);
        generarMapaCielo(scene, renderer);
        add_light(scene, renderer);
        extension_terrain(terrainMesh);

        // =========================================================================
        // 🔥 SECUENCIA CRÍTICA DE FLUJO DE MATRICES Y COLOR DE TERRENO
        // =========================================================================
        if (terrainMesh) {
            terrainMesh.geometry.computeVertexNormals();
            color_terrain_arena(terrainMesh, scene);
            scene.updateMatrixWorld(true); 
            //actualizarVientoPalmeras = populate_palms_eco(terrainMesh, scene, 800);
        }

        renderManager.needsRender = true;
      },
      (xhr) => {
        if (xhr.total) {
          const percent = Math.round((xhr.loaded / xhr.total) * 100);
          preloaderText.innerText = `Cozumel Map\nLoading: ${percent}%`;
          loaderProgress.style.width = percent + '%';
        }
      },
      (error) => { console.error('Error al cargar FBX:', error); }
    );

    window.addEventListener('resize', onWindowResize);
    renderer.setAnimationLoop(animate);
}

window.addEventListener('dblclick', (event) => {
  if (handleTerrainDoubleClick(event, renderManager.camera, renderManager.controls, raycaster, mouse, collidableMeshes, surfaceGroup, renderManager.surfaceY, [], {"data": []}, {"data": []}, null)) {
    renderManager.needsRender = true;
  }
});

const DISTANCIA_VISUALIZACION = 0.15;
let lastFrameTime = 0;

function animate(now) {
  const delta = now - lastFrameTime;
  if (delta < frameInterval) return;
  lastFrameTime = now;

  const deltaTimeSeconds = delta / 1000;

  if (renderManager.controls) {
    renderManager.controls.dampingFactor = (fps < 50) ? 0.05 : 0.08;
    renderManager.controls.update();
  }

  if (updatePivot && performance.now() - lastUserMove > PIVOT_DELAY) {
    const direction = new THREE.Vector3();
    renderManager.camera.getWorldDirection(direction);
    const desiredTarget = new THREE.Vector3().copy(renderManager.camera.position).add(direction.multiplyScalar(pivotDistance));
    renderManager.controls.target.lerp(desiredTarget, lerpPivotFactor);
  }

  if (typeof marcadoresRutas !== 'undefined') {
    marcadoresRutas.forEach((marcador) => {
      const letrero = marcador.letrero;
      if (letrero) letrero.visible = renderManager.camera.position.distanceTo(letrero.position) < DISTANCIA_VISUALIZACION;
    });
  }

  if (typeof marcadoresRutas_fuera_agua !== 'undefined') {
    marcadoresRutas_fuera_agua.forEach((marcador) => {
      const letrero = marcador.letrero;
      if (letrero) letrero.visible = renderManager.camera.position.distanceTo(letrero.position) < DISTANCIA_VISUALIZACION;
    });
  }

  if (actualizarVientoPalmeras) {
    actualizarVientoPalmeras(renderManager.camera);
    renderManager.needsRender = true; 
  }

  if (checkTerrainCollision(renderManager.camera, terrainMesh, raycaster, deltaTimeSeconds)) {
    renderManager.needsRender = true;
    if (renderManager.controls) renderManager.controls.update();
  }

  renderer.render(scene, renderManager.camera);
}

function onWindowResize() {
  renderManager.camera.aspect = window.innerWidth / window.innerHeight;
  renderManager.camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderManager.needsRender = true;
}

function home(){
   window.location.href = URL_GLOBAL+"Index/go_home_dashboard";
}

window.init = init;